import axios from 'axios';
import authService from './authService';
import router from '@/router';
import { API_URL } from '@/config/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = authService.token.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Só faz logout se a rota atual requer autenticação
      const currentRoute = router.currentRoute;
      const requiresAuth = currentRoute.matched.some(record => record.meta.requiresAuth === true);

      if (requiresAuth) {
        authService.logout();
      }
    }
    return Promise.reject(error);
  }
);

export default {
  /**
   * Criar um novo pagamento e retornar a URL de checkout do Mercado Pago
   * @param {object} paymentData - Dados do pagamento
   * @param {number} paymentData.giftId - ID do presente que está sendo pago (obrigatório)
   * @param {number} paymentData.amount - Valor do pagamento (obrigatório, máximo 10 dígitos, 2 casas decimais)
   * @param {string} paymentData.payerName - Nome completo do pagador (obrigatório, máximo 100 caracteres)
   * @param {string} paymentData.email - Email do pagador (obrigatório, deve ser um email válido)
   * @param {string} [paymentData.message] - Mensagem personalizada do pagador (opcional)
   * @param {string} [paymentData.phone] - Telefone do pagador (opcional)
   * @returns {Promise<object>} Resposta com URL de checkout
   * @returns {string} returns.checkoutUrl - URL de checkout do Mercado Pago para redirecionamento do usuário
   * @throws {Error} MPException - Erro genérico da API do Mercado Pago
   * @throws {Error} MPApiException - Erro na comunicação com a API do Mercado Pago
   */
  createPayment(paymentData) {
    return api.post('/payments/create-payment', paymentData);
  },

  // --- Visao administrativa: o que ja recebemos ---------------------------
  // Prefixo /wedding-payments, e nao /payments: aquele e publico no backend
  // porque serve o checkout. Estes exigem token.

  /**
   * Resumo do que ja entrou (aprovado, pendente, presentes, bingo)
   * @returns {Promise<object>} totalReceived, receivedCount, totalPending,
   *   pendingCount, giftsReceived, giftsReceivedCount, bingoReceived,
   *   bingoKitsSold, bingoCardsSold
   */
  getAdminReport() {
    return api.get('/wedding-payments/report');
  },

  /**
   * Lista pagamentos de presente e de bingo
   * @param {string} [status] - PENDING | APPROVED | REJECTED | CANCELLED
   */
  getAdminPayments(status) {
    return api.get('/wedding-payments', { params: status ? { status } : {} });
  },

  /**
   * Registra um presente como pago fora do Mercado Pago
   * @param {object} data
   * @param {number} data.giftId
   * @param {number} data.amount
   * @param {string} data.payerName
   * @param {string} data.method - PIX | DINHEIRO | OUTRO
   */
  markGiftAsPaid(data) {
    return api.post('/wedding-payments/manual', data);
  },

  /** Desfaz um pagamento registrado na mao */
  undoManualPayment(paymentId) {
    return api.delete(`/wedding-payments/${paymentId}`);
  },

  /**
   * Buscar todos os pagamentos pendentes para um convidado específico
   * @param {object} contactData - Dados de contato do convidado
   * @param {string} [contactData.email] - Email do convidado (opcional, mas pelo menos um dos dois é obrigatório)
   * @param {string} [contactData.telephone] - Telefone do convidado (opcional, mas pelo menos um dos dois é obrigatório)
   * @returns {Promise<Array>} Lista de pagamentos pendentes com links de checkout regenerados
   * @returns {number} returns[].paymentId - ID único do pagamento no banco de dados
   * @returns {string} returns[].giftName - Nome do presente (snapshot histórico)
   * @returns {number} returns[].amount - Valor do pagamento
   * @returns {string} returns[].status - Status do pagamento (sempre "PENDING" neste endpoint)
   * @returns {string} returns[].createdAt - Data e hora de criação do pagamento
   * @returns {string} returns[].paymentLinkCreatedAt - Data e hora de criação do link de pagamento
   * @returns {string} returns[].checkoutUrl - URL de checkout regenerada para o pagamento
   */
  getPendingPayments(contactData) {
    return api.post('/payments/pending', contactData);
  },

  /**
   * Verificar se um pagamento está pendente
   * @param {object} payment - Objeto do pagamento
   * @returns {boolean} True se o pagamento está pendente, false caso contrário
   */
  isPaymentPending(payment) {
    return payment.status === 'PENDING';
  },

  /**
   * Verificar se um pagamento foi aprovado
   * @param {object} payment - Objeto do pagamento
   * @returns {boolean} True se o pagamento foi aprovado, false caso contrário
   */
  isPaymentApproved(payment) {
    return payment.status === 'APPROVED';
  },

  /**
   * Verificar se um pagamento foi rejeitado
   * @param {object} payment - Objeto do pagamento
   * @returns {boolean} True se o pagamento foi rejeitado, false caso contrário
   */
  isPaymentRejected(payment) {
    return payment.status === 'REJECTED';
  },

  /**
   * Verificar se um pagamento foi cancelado
   * @param {object} payment - Objeto do pagamento
   * @returns {boolean} True se o pagamento foi cancelado, false caso contrário
   */
  isPaymentCancelled(payment) {
    return payment.status === 'CANCELLED';
  },

  /**
   * Obter descrição do status do pagamento
   * @param {string} status - Status do pagamento
   * @returns {string} Descrição do status em português
   */
  getPaymentStatusDescription(status) {
    const statusMap = {
      'PENDING': 'Pagamento aguardando confirmação',
      'APPROVED': 'Pagamento aprovado',
      'REJECTED': 'Pagamento rejeitado',
      'CANCELLED': 'Pagamento cancelado'
    };
    return statusMap[status] || 'Status desconhecido';
  },

  /**
   * Redirecionar para a URL de checkout do Mercado Pago
   * @param {string} checkoutUrl - URL de checkout do Mercado Pago
   */
  redirectToCheckout(checkoutUrl) {
      const newTab = window.open('', '_blank');

      newTab.location.href = checkoutUrl;
  }
};
